/**
 * Dice tray — Domain layer behind the header's "Zar Kokpiti" popover.
 *
 * The tray rolls TWO systems in one place: Fate's 4dF ladder roll and the
 * 5e-style NdX test. Every number the popover shows (and every line the
 * history shows) is computed here, pure and with injectable randomness, so
 * the React component only formats and the smoke gate can replay a roll.
 *
 * Why this file exists next to `domain/dice.ts`: dice.ts is the GENERIC
 * notation engine (parse / keep / drop / advantage). This module is the
 * tray's POLICY on top of it — which notation the tray builds, how the
 * environmental chips modify a roll, when a natural 20 counts as a crit
 * (including under advantage, where dice.ts deliberately reports none),
 * and how the history is capped.
 *
 * Environmental chips are a TRAY convention, not a transcription of any
 * book: the popover prints exactly what the selected chip will do before
 * you roll (see ENV_TEXT_HINT in the UI), so nothing is applied silently.
 *   · 🌕 full moon  → +2 (the water boost)           — both modes
 *   · 🌑 eclipse    → −2 (bending is blocked)        — both modes
 *   · ☄️ comet      → fire DAMAGE dice ×10 (d4–d12);
 *                     inert on a d20 test; +2 on the Fate ladder
 */

import { rollNotation, type Random, type RollResult } from '@/domain/dice';

export type TrayMode = 'fate' | 'dnd';

/** One Fate die: +, 0 or −. */
export type FateDie = -1 | 0 | 1;

export type FateOutcome = 'fail' | 'tie' | 'succeed' | 'style';

export type EnvId = 'full-moon' | 'comet' | 'eclipse';

export const FATE_DIE_COUNT = 4;
export const FATE_LADDER_MIN = -4;
export const FATE_LADDER_MAX = 8;

/** The dice the tray offers (d100 is left out: no rule here rolls a d100). */
export const TRAY_DICE = [4, 6, 8, 10, 12, 20] as const;
export const TRAY_MAX_COUNT = 10;
export const TRAY_HISTORY_LIMIT = 10;

/* ------------------------------------------------------------------ Fate */

export interface FateRoll {
  dice: FateDie[];
  /** Skill rank, before the environmental chip. */
  skill: number;
  /** Flat modifier the chip adds (0 when no chip is selected). */
  envBonus: number;
  env: EnvId | null;
  /** Ladder value before clamping — what the margin is measured from. */
  rawTotal: number;
  /** The displayed rung: Fate's ladder stops at +8 / −4. */
  ladderValue: number;
  target: number;
  /** rawTotal − target: the whole outcome hangs off this number. */
  margin: number;
  outcome: FateOutcome;
  /** `4dF`, `4dF+2`, `4dF−3` — echoed into the history. */
  notation: string;
}

/** Sign as printed on a roll: `+2`, `−3`, nothing at 0. */
export function formatModifier(modifier: number): string {
  if (modifier === 0) return '';
  return modifier > 0 ? `+${modifier}` : `−${Math.abs(modifier)}`;
}

/**
 * The SAME sign in notation syntax: ASCII `+`/`-`, because this is fed back
 * into `parseNotation`, which only understands `[+-]\d+` (the typographic
 * minus of formatModifier is for eyes, not for the parser).
 */
export function notationModifier(modifier: number): string {
  if (modifier === 0) return '';
  return modifier > 0 ? `+${modifier}` : `-${Math.abs(modifier)}`;
}

export function clampLadder(value: number): number {
  return Math.max(FATE_LADDER_MIN, Math.min(FATE_LADDER_MAX, value));
}

/**
 * The four-fantasy-dice roll. `dice` may be injected so a test (or a re-roll
 * of the same outcome) does not have to trust Math.random.
 */
export function rollFateDice(random: Random = Math.random): FateDie[] {
  return Array.from({ length: FATE_DIE_COUNT }, () => {
    // three equiprobable faces: -1, 0, +1
    const face = Math.floor(random() * 3) - 1;
    return (face < -1 ? -1 : face > 1 ? 1 : face) as FateDie;
  });
}

/** Fate's four result bands, straight off the margin (total − target). */
export function fateOutcome(margin: number): FateOutcome {
  if (margin < 0) return 'fail';
  if (margin === 0) return 'tie';
  if (margin >= 3) return 'style';
  return 'succeed';
}

export function fateRoll(input: {
  skill: number;
  target: number;
  env?: EnvId | null;
  dice?: FateDie[];
  random?: Random;
}): FateRoll {
  const env = input.env ?? null;
  const dice = input.dice ?? rollFateDice(input.random ?? Math.random);
  const envBonus = envEffect(env, 'fate').bonus;
  const skill = Number.isFinite(input.skill) ? input.skill : 0;
  const target = Number.isFinite(input.target) ? input.target : 0;
  const rawTotal = dice.reduce<number>((sum, die) => sum + die, 0) + skill + envBonus;
  const margin = rawTotal - target;

  return {
    dice,
    skill,
    envBonus,
    env,
    rawTotal,
    ladderValue: clampLadder(rawTotal),
    target,
    margin,
    outcome: fateOutcome(margin),
    notation: `4dF${formatModifier(skill + envBonus)}`,
  };
}

/* ------------------------------------------------------- environmental */

export interface EnvEffect {
  /** Flat modifier added to the total (Fate rung / 5e total). */
  bonus: number;
  /** Multiplier applied to the DICE total only — never to the modifier. */
  multiplier: number;
}

/**
 * What the selected chip actually does, per mode. `sides` only matters for
 * the comet: ×10 is a damage rule, so it never touches a d20 test.
 */
export function envEffect(env: EnvId | null, mode: TrayMode, sides = 20): EnvEffect {
  if (env === null) return { bonus: 0, multiplier: 1 };
  if (env === 'full-moon') return { bonus: 2, multiplier: 1 };
  if (env === 'eclipse') return { bonus: -2, multiplier: 1 };
  // comet
  if (mode === 'fate') return { bonus: 2, multiplier: 1 };
  return sides === 20 ? { bonus: 0, multiplier: 1 } : { bonus: 0, multiplier: 10 };
}

/* ------------------------------------------------------------------ 5e */

export type AdvantageChoice = 'none' | 'adv' | 'dis';

/**
 * The notation the tray rolls (and previews under the button). Advantage is
 * only ever attached to a SINGLE d20 — anywhere else it would silently mean
 * nothing, so the tray drops it here and the UI greys the control out.
 */
export function dndNotation(input: {
  sides: number;
  count?: number;
  modifier?: number;
  advantage?: AdvantageChoice;
}): string {
  const sides = input.sides;
  const count = Math.max(1, Math.min(TRAY_MAX_COUNT, input.count ?? 1));
  const modifier = Number.isFinite(input.modifier) ? (input.modifier as number) : 0;
  const advantage = input.advantage ?? 'none';
  // Advantage means rolling TWO d20s and keeping one — so the count doubles,
  // the notation gains a keep rule, and everything else stays untouched.
  const withAdvantage = sides === 20 && count === 1 && advantage !== 'none';
  const diceCount = withAdvantage ? 2 : count;
  const keep = withAdvantage ? (advantage === 'adv' ? 'kh1' : 'kl1') : '';
  return `${diceCount}d${sides}${keep}${notationModifier(modifier)}`;
}

/**
 * Read a half-typed number field safely: `''`, `'-'` and `'12abc'` all fall
 * back instead of poisoning the roll with NaN, and the result is clamped.
 */
export function intOf(text: string, fallback: number, min: number, max: number): number {
  const trimmed = text.trim();
  if (trimmed === '' || trimmed === '-' || trimmed === '+') return fallback;
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
}

export interface DndTrayRoll {
  /** The notation actually rolled (advantage only applies to one d20). */
  notation: string;
  result: RollResult;
  env: EnvEffect;
  /** diceTotal × multiplier + modifier + bonus. */
  total: number;
  /** The kept natural face on a d20 roll, or null for other dice. */
  natural: number | null;
  critical: 'hit' | 'miss' | null;
}

/**
 * Roll the tray's 5e test. Advantage is requested as a separate argument
 * (not baked into the notation) because it is only meaningful for a single
 * d20 — anywhere else the tray rolls straight and says so.
 */
export function rollDndTray(input: {
  sides: number;
  count?: number;
  modifier?: number;
  advantage?: AdvantageChoice;
  env?: EnvId | null;
  random?: Random;
}): DndTrayRoll {
  const sides = input.sides;
  const count = Math.max(1, Math.min(TRAY_MAX_COUNT, input.count ?? 1));
  const modifier = Number.isFinite(input.modifier) ? (input.modifier as number) : 0;
  const random = input.random ?? Math.random;
  const env = input.env ?? null;

  const advantage: AdvantageChoice = input.advantage ?? 'none';
  const notation = dndNotation({ sides, count, modifier, advantage });

  const result = rollNotation(notation, random);
  if (!result) {
    // Unreachable: notation is built from validated numbers above. Guarded so
    // the caller never has to null-check a roll the tray itself constructed.
    throw new Error(`dice tray built an unparseable notation: ${notation}`);
  }

  const envApplied = envEffect(env, 'dnd', sides);
  const total =
    result.diceTotal * envApplied.multiplier + modifier + envApplied.bonus;

  const kept = result.dice.filter((die) => die.kept);
  const natural = sides === 20 && kept.length === 1 ? (kept[0]?.value ?? null) : null;

  return {
    notation,
    result,
    env: envApplied,
    total,
    natural,
    critical: natural === 20 ? 'hit' : natural === 1 ? 'miss' : null,
  };
}

/* --------------------------------------------------------------- display */

/** `[17, (4)]` — kept faces bare, the discarded one bracketed, never hidden. */
export function dndFaces(roll: DndTrayRoll): string {
  return roll.result.dice.map((die) => (die.kept ? `${die.value}` : `(${die.value})`)).join(', ');
}

/** Fate faces, spaced and signed: `+1 −1 0 0`. */
export function fateFaces(dice: readonly FateDie[]): string {
  return dice.map((die) => (die > 0 ? '+1' : die < 0 ? '−1' : '0')).join(' ');
}

/**
 * The chip's mark exactly as the history stamps it — `🌕 +2`, `☄️ ×10`,
 * `🌑 −2`, or '' with no chip. Icons and figures are locale-free by design.
 */
export function envTag(env: EnvId | null, effect: EnvEffect): string {
  if (env === null) return '';
  if (env === 'full-moon') return `🌕 ${effect.bonus >= 0 ? '+' : ''}${effect.bonus}`;
  if (env === 'eclipse') return `🌑 ${effect.bonus >= 0 ? '+' : ''}${effect.bonus}`;
  return effect.multiplier !== 1 ? `☄️ ×${effect.multiplier}` : `☄️ ${effect.bonus >= 0 ? '+' : ''}${effect.bonus}`;
}

/* --------------------------------------------------------------- history */

export interface TrayRoll {
  /** Monotonic, newest-first — also the React key. */
  id: number;
  mode: TrayMode;
  /** `4dF+2` / `3d6+4` / `2d20kh1+5` — what was rolled. */
  notation: string;
  /** The formatted breakdown the tray showed (dice faces included). */
  line: string;
  total: number;
  tone: 'crit' | 'fumble' | 'normal';
  env: EnvId | null;
  /** `Date.now()` when the tray rolled it — stamped by the caller, not here. */
  at: number;
}

/** Id for the next entry: 1, 2, 3 … whatever the newest currently holds. */
export function nextRollId(history: readonly TrayRoll[]): number {
  return (history[0]?.id ?? 0) + 1;
}

/** Newest first, capped — the oldest entry falls off the end. */
export function pushRoll(
  history: readonly TrayRoll[],
  roll: TrayRoll,
  limit = TRAY_HISTORY_LIMIT,
): TrayRoll[] {
  return [roll, ...history].slice(0, Math.max(1, limit));
}
